/**
 * Staff self-registration — the public form.
 *
 * This is the link a tenant's employees receive by text or email ("register
 * your car for parking at work"), so it is deliberately NOT behind a sign-in:
 * a login in front of it defeats the point, and the person filling it in has no
 * Parkify account and never will. It is the only unauthenticated screen in this
 * app, which is why it does exactly one thing and shows nothing it wasn't given.
 *
 * Submitting GRANTS NOTHING — it queues a pending request that the tenant's own
 * manager approves. The copy says so plainly at the end, because "you're
 * registered" would read as "you can park now", and a staff member who believes
 * that gets a citation.
 *
 * Phone-first: every real user of this screen is standing in a break room with
 * their phone, so it's a single narrow column with large tap targets, not a
 * desktop form scaled down.
 */
import { useEffect, useState, type CSSProperties } from 'react'
import { getRegisterInfo, submitRegistration, type PlateStateInput, type RegisterInfo } from '../lib/api'
import { DEFAULT_PERMIT_TYPE, TYPE_LABEL } from '../lib/permitTypes'
import { ghostBtn, labelStyle as baseLabel, submitBtn as purpleBtn } from '../ui'
import { PLATE_STATE_GROUPS, isPlateStateCode } from '@parkify/ui'
import { PhoneInput } from '../../../../packages/account/src/PhoneInput'
import { PARKER_PHONE_ERROR, normalizeParkerPhone } from '../../../../packages/account/src/parkerPhone'
import { requestSentCopy } from '../lib/registrationQueue'

/* Same treatment as every other screen's label; this one keeps its own
   bottom margin (and Register its `display`), which is spacing, not type. */
const labelStyle: CSSProperties = { ...baseLabel,
  marginBottom: 5, display: 'block', }

const inputStyle: CSSProperties = {
  width: '100%', padding: '11px 13px', borderRadius: 9, border: '1.5px solid var(--color-gray-300)',
  fontSize: 16, fontWeight: 600, fontFamily: 'inherit', boxSizing: 'border-box', background: '#fff',
}
const errText: CSSProperties = { fontSize: 12, color: '#b91c1c', marginTop: 5, fontWeight: 600 }

type Field = 'firstName' | 'lastName' | 'plate' | 'issuingState' | 'phone' | 'email'

export function Register({ tenantId, permitType, programId, permitId, reserved = false }: { tenantId: string; permitType?: string; programId?: string; permitId?: string; reserved?: boolean }) {
  const [info, setInfo] = useState<RegisterInfo | null>(null)
  const [loadErr, setLoadErr] = useState('')
  const [f, setF] = useState<Record<Field, string>>({ firstName: '', lastName: '', plate: '', issuingState: '', phone: '', email: '' })
  const [additionalPlateStates, setAdditionalPlateStates] = useState<PlateStateInput[]>([])
  const [errs, setErrs] = useState<Partial<Record<Field, string>>>({})
  const [busy, setBusy] = useState(false)
  const [submitErr, setSubmitErr] = useState('')
  const [done, setDone] = useState<{ duplicate: boolean; blocked: boolean; status: string } | null>(null)
  // The server defaults an unnamed link to Employee, so the common link still
  // names the permit in the same way Austin's registration view does. Unknown
  // values remain unnamed rather than making a claim about where the request lands.
  const knownType = permitType ? TYPE_LABEL[permitType] : TYPE_LABEL[DEFAULT_PERMIT_TYPE]

  useEffect(() => {
    void getRegisterInfo(tenantId, programId, reserved, permitId)
      .then(setInfo)
      .catch(() => setLoadErr('This registration link is not valid. Ask your manager for a new one.'))
  }, [tenantId, programId, reserved, permitId])
  // A program link names the shared program, which is what the request lands
  // in; the type label is for ordinary links only.
  // A named-permit link names the permit itself (the server confirmed it is the tenant's).
  const programLabel = info?.programName
    ? `${info.programName} (shared program${info.reservedBay ? ' · reserved bay' : ''})`
    : info?.permitName ?? null

  // Typing in a field clears ITS OWN error and nothing else — re-running every
  // rule on every keystroke would flash errors for fields nobody has reached.
  const set = (k: Field) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = k === 'plate' ? e.target.value.toUpperCase() : e.target.value
    setF(prev => ({ ...prev, [k]: v }))
    if (errs[k]) setErrs(prev => ({ ...prev, [k]: undefined }))
  }

  // Everything is checked on the SAME press, so one submit surfaces all of it
  // rather than making someone discover the problems one at a time.
  const validate = (): boolean => {
    const next: Partial<Record<Field, string>> = {}
    if (!f.firstName.trim()) next.firstName = 'Required'
    if (!f.lastName.trim()) next.lastName = 'Required'
    const plateStates = [{ plate: f.plate, state: f.issuingState }, ...additionalPlateStates]
    const plates = plateStates.map(value => value.plate.replace(/[^A-Za-z0-9]/g, '').toUpperCase())
    if (plates[0]!.length < 2) next.plate = 'Enter your license plate'
    if (plateStates.some(vehicle => !isPlateStateCode(vehicle.state))) next.issuingState = 'Pick the state or province for each license plate'
    if (plates.slice(1).some(plate => plate && (plate.length < 2 || plate.length > 12))) next.plate = 'Check each license plate'
    if (new Set(plates.filter(Boolean)).size !== plates.filter(Boolean).length) next.plate = 'Each license plate must be unique'
    if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) next.email = 'Check this address'
    if (!normalizeParkerPhone(f.phone)) next.phone = PARKER_PHONE_ERROR
    setErrs(next)
    return Object.keys(next).length === 0
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy || !validate()) return
    setBusy(true); setSubmitErr('')
    try {
      const res = await submitRegistration({
        // Carried from the link, not collected from the applicant.
        ...(permitType ? { permitType } : {}),
        ...(programId ? { programId } : {}),
        ...(!programId && info?.permitId ? { permitId: info.permitId } : {}),
        ...(programId && info?.reservedBay ? { reservedBay: true } : {}),
        tenantId,
        firstName: f.firstName.trim(),
        lastName: f.lastName.trim(),
        plate: f.plate.trim(),
        issuingState: f.issuingState,
        ...(additionalPlateStates.length ? { plates: additionalPlateStates.map(vehicle => vehicle.plate).filter(Boolean) } : {}),
        plateStates: [{ plate: f.plate, state: f.issuingState }, ...additionalPlateStates],
        phone: normalizeParkerPhone(f.phone) ?? '',
        ...(f.email.trim() ? { email: f.email.trim() } : {}),
      })
      setDone({ duplicate: res.duplicate, blocked: res.blocked, status: res.status })
    } catch (err) {
      setSubmitErr(err instanceof Error ? err.message : 'Could not send that just now. Try again in a moment.')
    } finally { setBusy(false) }
  }

  const accent = info?.brandColor && /^#[0-9a-fA-F]{6}$/.test(info.brandColor) ? info.brandColor : '#7F3F98'

  return (
    <div style={{ minHeight: '100vh', background: '#FCFBFD', display: 'flex', flexDirection: 'column' }}>
      <header style={{ background: '#fff', borderBottom: '1px solid var(--color-gray-200)', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <img src="/parkify-logo-color.svg" alt="Parkify" style={{ height: 20, width: 'auto', display: 'block' }} />
      </header>

      <main style={{ flex: 1, width: '100%', maxWidth: 460, margin: '0 auto', padding: '34px 22px 30px', background: '#FCFBFD', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        {loadErr ? (
          <div style={{ background: 'rgba(237,28,36,0.10)', border: '1.5px solid rgba(237,28,36,0.4)', borderRadius: 12, padding: '16px 18px', fontSize: 14, color: 'var(--color-gray-700)', lineHeight: 1.55 }}>
            {loadErr}
          </div>
        ) : !info ? (
          <div style={{ fontSize: 14, color: 'var(--color-gray-600)', textAlign: 'center', padding: '40px 0' }}>Loading…</div>
        ) : done ? (
          <>
            <div style={{ textAlign: 'center', padding: '14px 0 20px' }}>
              <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(98,197,132,0.16)', color: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, fontWeight: 800, margin: '0 auto 14px' }}>✓</div>
              <h1 style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.4px', margin: '0 0 6px' }}>
                {done.blocked ? 'Registration declined' : done.duplicate ? "You're already on the list" : `Request sent, ${f.firstName.trim()}`}
              </h1>
              {/* The honest beat: this is a REQUEST. Saying "you're registered"
                  here would read as "you can park now", and someone who believes
                  that parks on an unapproved plate and gets a ticket. The normal
                  success state follows the demo's request-sent copy; duplicate
                  and declined responses keep their production-specific guidance. */}
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-600)', lineHeight: 1.55, maxWidth: 300, margin: '0 auto' }}>
                {done.blocked
                  ? <>This plate was declined by your manager and can't be registered again. Ask them if that was a mistake.</>
                  : done.duplicate
                  ? <>We already have <b>{f.plate.toUpperCase()}</b> waiting for review at {info.name}. Your manager still needs to approve it.</>
                  : <>{requestSentCopy(info.name, Boolean(f.email?.trim()))}</>}
              </p>
            </div>
            {!done.blocked && !done.duplicate && (
              <div style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 14, overflow: 'hidden', marginTop: 14 }}>
                {[
                  [additionalPlateStates.length ? 'Plates' : 'Plate', [f.plate, ...additionalPlateStates.map(vehicle => vehicle.plate)].filter(Boolean).join(' · ')],
                  ['Permit', programLabel ?? (knownType ? `${knownType} permit` : 'Permit')],
                  ['Status', 'Waiting on approval'],
                ].map(([label, value], index) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '13px 15px', borderTop: index ? '1px solid var(--color-gray-200)' : 'none' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-gray-600)' }}>{label}</span>
                    <span style={{ fontSize: 13.5, fontWeight: 800, color: label === 'Status' ? '#c2410c' : 'var(--color-gray-900)', fontFamily: label === 'Plate' || label === 'Plates' ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit', letterSpacing: label === 'Plate' || label === 'Plates' ? '.04em' : undefined, textAlign: 'right' }}>{value}</span>
                  </div>
                ))}
              </div>
            )}
            {!done.blocked && (
              <button type="button" onClick={() => {
                setF({ firstName: '', lastName: '', plate: '', issuingState: '', phone: '', email: '' })
                setAdditionalPlateStates([])
                setDone(null)
              }} style={{ ...ghostBtn, width: '100%', marginTop: 14, cursor: 'pointer' }}>
                Register another vehicle
              </button>
            )}
          </>
        ) : (
          <>
            <div style={{ textAlign: 'left', marginBottom: 22 }}>
              <div style={{ color: '#7F3F98', fontSize: 15, fontWeight: 800, marginBottom: 22 }}>{info.name}</div>
              <h1 style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.4px', margin: '0 0 10px' }}>Register for parking</h1>
              {/* WHICH permit this link asks for.
                  The type rides in on the link (`&type=`) and was submitted
                  silently: a Vendor link and a staff link rendered the identical
                  form, so nobody — applicant or manager — could tell which one
                  somebody had actually used. Naming it makes a forwarded wrong
                  link visible at the only moment it's cheap to fix.

                  ⚠️ ONLY FOR A TYPE WE KNOW. The param is advisory — the server
                  re-validates it against a closed union and files an unknown one
                  under the default bucket. `typeLabel()` passes an unknown key
                  through verbatim, so using it here would print "gold permit"
                  over a request that lands in Staff. An unnamed chip is a gap; a
                  wrongly-named one is a false statement about where the request
                  goes, so an unrecognised type renders nothing at all. */}
              {(programLabel || knownType) && (
                <div style={{
                  display: 'inline-block', padding: '5px 12px', borderRadius: 999,
                  background: 'rgba(127,63,152,0.08)', border: '1px solid rgba(127,63,152,0.18)',
                  fontSize: 12.5, fontWeight: 700, color: '#7F3F98',
                }}>
                  {programLabel ?? `${knownType} permit`}
                </div>
              )}
              {info?.reservedBay && (
                <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', margin: '10px 0 0', lineHeight: 1.5 }}>
                  This sign-up is for a <b>reserved numbered bay</b>. Your manager assigns the bay when they approve you; until then, nothing is held.
                </p>
              )}
            </div>

            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', gap: 9 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={labelStyle} htmlFor="reg-first">First name</label>
                  <input id="reg-first" value={f.firstName} onChange={set('firstName')} autoComplete="given-name"
                    style={{ ...inputStyle, borderColor: errs.firstName ? '#b91c1c' : undefined }} />
                  {errs.firstName && <div style={errText}>{errs.firstName}</div>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={labelStyle} htmlFor="reg-last">Last name</label>
                  <input id="reg-last" value={f.lastName} onChange={set('lastName')} autoComplete="family-name"
                    style={{ ...inputStyle, borderColor: errs.lastName ? '#b91c1c' : undefined }} />
                  {errs.lastName && <div style={errText}>{errs.lastName}</div>}
                </div>
              </div>

              <div>
                {[{ plate: f.plate, state: f.issuingState }, ...additionalPlateStates].map((vehicle, index) => (
                  <div className="plate-state-row" key={index} style={{ gap: 8, alignItems: 'end', marginTop: index ? 10 : 0 }}>
                    <div>
                      <label style={labelStyle} htmlFor={index === 0 ? 'reg-plate' : `reg-plate-${index + 1}`}>
                        {index === 0 ? 'License plate' : 'Additional plate'}
                      </label>
                      <input
                        id={index === 0 ? 'reg-plate' : `reg-plate-${index + 1}`}
                        value={vehicle.plate}
                        onChange={index === 0 ? set('plate') : event => setAdditionalPlateStates(prev => prev.map((value, i) => i === index - 1 ? { ...value, plate: event.target.value.toUpperCase() } : value))}
                        autoCapitalize="characters" autoCorrect="off" spellCheck={false}
                        placeholder={index === 0 ? 'ABC 1234' : undefined}
                        style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace', letterSpacing: '1px', borderColor: errs.plate ? '#b91c1c' : undefined }}
                      />
                    </div>
                    <div>
                      <label style={labelStyle} htmlFor={`reg-plate-state-${index + 1}`}>State / province</label>
                      <select
                        id={`reg-plate-state-${index + 1}`}
                        aria-label={`State / province for plate ${index + 1}`}
                        value={vehicle.state}
                        onChange={event => {
                          if (index === 0) setF(prev => ({ ...prev, issuingState: event.target.value }))
                          else setAdditionalPlateStates(prev => prev.map((value, i) => i === index - 1 ? { ...value, state: event.target.value } : value))
                          if (errs.issuingState) setErrs(prev => ({ ...prev, issuingState: undefined }))
                        }}
                        required
                        style={{ ...inputStyle, borderColor: errs.issuingState ? '#b91c1c' : undefined }}
                      >
                        <option value="">Select a state</option>
                        {PLATE_STATE_GROUPS.map(group => (
                          <optgroup key={group.label} label={group.label}>
                            {group.options.map(option => <option key={option.code} value={option.code}>{option.label}</option>)}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    {index > 0 && <button className="plate-state-remove" type="button" aria-label={`Remove plate ${index + 1}`} onClick={() => setAdditionalPlateStates(prev => prev.filter((_, i) => i !== index - 1))} style={{ ...ghostBtn, padding: '10px 8px' }}>Remove</button>}
                  </div>
                ))}
                {errs.plate && <div style={errText}>{errs.plate}</div>}
                {errs.issuingState && <div style={errText}>{errs.issuingState}</div>}
                {additionalPlateStates.length < (info.platesPerParker ?? 3) - 1 && <button type="button" onClick={() => setAdditionalPlateStates(prev => [...prev, { plate: '', state: '' }])} style={{ marginTop: 10, border: 0, padding: 0, background: 'transparent', color: '#7F3F98', fontWeight: 800, cursor: 'pointer' }}>+ Add another plate</button>}
              </div>

              <div>
                <label style={labelStyle} htmlFor="reg-phone">Phone number</label>
                <PhoneInput id="reg-phone" overflow="keep" value={f.phone} placeholder="(704) 555-0100"
                  // Same contract as `set`: typing clears THIS field's error and nothing else.
                  onChange={value => { setF(prev => ({ ...prev, phone: value })); if (errs.phone) setErrs(prev => ({ ...prev, phone: undefined })) }}
                  aria-invalid={errs.phone ? true : undefined}
                  style={{ ...inputStyle, borderColor: errs.phone ? '#b91c1c' : undefined }} />
                {errs.phone && <div style={errText}>{errs.phone}</div>}
              </div>

              <div>
                <label style={labelStyle} htmlFor="reg-email">Email <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 600 }}>(optional)</span></label>
                <input id="reg-email" value={f.email} onChange={set('email')} type="email" autoComplete="email" placeholder="you@company.com"
                  style={{ ...inputStyle, borderColor: errs.email ? '#b91c1c' : undefined }} />
                {errs.email && <div style={errText}>{errs.email}</div>}
              </div>

              {submitErr && (
                <div style={{ background: 'rgba(237,28,36,0.10)', border: '1px solid rgba(237,28,36,0.35)', borderRadius: 10, padding: '10px 12px', fontSize: 13, color: '#b91c1c', lineHeight: 1.5 }}>
                  {submitErr}
                </div>
              )}

              <button type="submit" disabled={busy} style={{ ...purpleBtn, background: accent, opacity: busy ? 0.6 : 1, cursor: busy ? 'default' : 'pointer' }}>
                {busy ? 'Registering…' : 'Register'}
              </button>
            </form>
          </>
        )}
      </main>
    </div>
  )
}
